import React, { useState, useRef, useEffect } from 'react';
import { useTheme, PALETTES } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, Globe, ChevronDown, LogIn, User } from 'lucide-react';
import Logo from '../common/Logo';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'EN', native: 'English' },
  { code: 'ar', label: 'عربي', native: 'العربية' },
  { code: 'fr', label: 'FR', native: 'Français' },
  { code: 'es', label: 'ES', native: 'Español' },
  { code: 'ur', label: 'اردو', native: 'اردو' },
];

const TopHeader = () => {
  const { isDarkMode, toggleTheme, language, setLanguage, isRTL, palette, cyclePalette } = useTheme();
  const { isAuthenticated, user, login } = useAuth();
  const { t } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const currentLang = LANGUAGE_OPTIONS.find(l => l.code === language) || LANGUAGE_OPTIONS[0];

  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${
      isDarkMode
        ? 'bg-[var(--bg-dark-from)]/95 backdrop-blur-md border-b border-white/10'
        : 'bg-white/95 backdrop-blur-md border-b border-gray-100'
    }`} style={{ boxShadow: isDarkMode ? 'none' : '0 1px 20px rgba(0,0,0,0.06)' }}>
      <div className="max-w-lg mx-auto px-3">
        <div className={`flex items-center justify-between h-14 ${isRTL ? 'flex-row-reverse' : ''}`}>

          {/* Logo + Title */}
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Logo size={28} />
            <span className={`text-sm font-bold tracking-tight hidden xs:block ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
              {t('settings.appTitle')}
            </span>
          </div>

          {/* Controls */}
          <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>

            {/* Language dropdown */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(p => !p)}
                data-testid="language-toggle-btn"
                className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{currentLang.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className={`absolute top-full mt-1.5 ${isRTL ? 'left-0' : 'right-0'} z-50 min-w-[148px] rounded-2xl shadow-xl border overflow-hidden animate-scale-in ${
                  isDarkMode ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'
                }`}>
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button key={lang.code} data-testid={`lang-option-${lang.code}`}
                      onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors text-left ${
                        language === lang.code
                          ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-[var(--p-hex)]/10 text-[var(--p-hex)] font-semibold'
                          : isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'
                      }`}>
                      <span className="font-semibold w-9 text-center">{lang.label}</span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{lang.native}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Palette cycle toggle */}
            <button
              data-testid="palette-cycle-btn"
              onClick={cyclePalette}
              title={PALETTES[palette].name}
              aria-label={`Theme colour: ${PALETTES[palette].name}. Click to cycle.`}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'bg-white/10 text-gray-300 hover:bg-white/20 ring-1 ring-white/15'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 ring-1 ring-gray-200'
              }`}
            >
              <span
                className="w-3.5 h-3.5 rounded-full flex-shrink-0 ring-2 ring-white/30"
                style={{ background: PALETTES[palette].swatch }}
              />
              <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {PALETTES[palette].name.split(' ')[0]}
              </span>
            </button>

            {/* Theme toggle */}
            <button onClick={toggleTheme} data-testid="theme-toggle"
              className={`p-2 rounded-full transition-colors ${
                isDarkMode ? 'bg-white/10 text-amber-400 hover:bg-white/20' : 'bg-gray-100 text-[var(--p-hex)] hover:bg-gray-200'
              }`} aria-label="Toggle theme">
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Auth state — Sign in (guest) or Avatar (authenticated) */}
            {isAuthenticated && user ? (
              <div className={`flex items-center gap-1.5 pl-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-[var(--p-hex)]/30 shadow-sm"
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-[var(--p-hex)]/10 text-[var(--p-hex)]'
                  }`}>
                    {firstName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="header-signin-btn"
                onClick={login}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105 ${
                  isDarkMode
                    ? 'bg-amber-500 text-[#0f172a] hover:bg-amber-400'
                    : 'bg-[var(--p-hex)] text-white hover:bg-[var(--p-light-hex)]'
                }`}
              >
                <LogIn className="w-3.5 h-3.5" />
                <span>{t('home.signIn')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
